<template>
  <div>
    <page-header :header="$t('company.your_prices')" />

    <div class="content">
      <PricingAlert></PricingAlert>
      <b-row>
        <b-col lg="4">
          <b-card>
            <div v-if="$allows('administer')">
              <field
                :field="fields.byAttribute.company_id"
                layout="vertical"
                @input="onCompanyChange"
              ></field>

              <div class="form-check">
                <input
                  id="has_pricing"
                  v-model="has_pricing"
                  :false-value="null"
                  type="checkbox"
                  class="form-check-input"
                />
                <label for="has_pricing">
                  {{ $t('company.has_pricing') }}
                </label>
              </div>
            </div>

            <field
              :field="fields.byAttribute.division_id"
              layout="vertical"
              @input="onDivisionChange"
            ></field>

            <field
              :field="fields.byAttribute.vehicle_id"
              layout="vertical"
            ></field>
          </b-card>

          <b-card
            v-if="dataLoaded"
            :title="$t('vehicles.field_groups.standard')"
          >
            <fields group="standard" />
          </b-card>

          <b-card
            v-if="dataLoaded"
            :title="$t('vehicles.field_groups.additional')"
          >
            <fields group="additional" />
          </b-card>

          <b-card v-if="dataLoaded" class="text-right">
            <b-button variant="primary" size="lg" @click="save">{{
              $t('general.save')
            }}</b-button>
          </b-card>
        </b-col>
        <b-col v-if="dataLoaded" lg="4">
          <b-card>
            <field name="pricing.per_minute"></field>
          </b-card>

          <b-card :title="$t('vehicles.attributes.pricing.price_per_km.label')">
            <fields group="price_per_km" />
          </b-card>
          <b-card
            :title="$t('vehicles.attributes.pricing.return_price_per_km.label')"
          >
            <fields group="return_price_per_km" />
          </b-card>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import ResourceFormMixin from '../stylemix-vue-admin/mixins/ResourceFormMixin'
import Api from './Api'
import PricingAlert from './PricingAlert.vue'
import { applyAverages, weekDays } from './utils'
import { mapState } from 'vuex'

export default {
  name: 'CityTaxiPricing',

  components: {
    PricingAlert,
  },

  mixins: [ResourceFormMixin],

  data() {
    return {
      formLoaded: false,
      dataLoaded: false,
      has_pricing: null,
    }
  },

  computed: {
    ...mapState('zones', ['currentDivision']),
    days: () => weekDays,
  },

  watch: {
    'model.company_id': function () {
      this.resetPricesData()
      this.loadDivisions()
    },
    'model.division_id': function () {
      this.loadVehicles()
    },
    'model.vehicle_id': function () {
      this.loadPrices()
    },
    currentDivision() {
      this.updateCompanyFilters()
    },
    has_pricing() {
      this.updateCompanyFilters()
    },
    formLoaded(val) {
      if (val) {
        this.loadPrices()
      }
    },
  },

  created() {
    this.loadForm().then(() => {
      this.model = { ...this.model, ...this.$route.params }
    })
  },

  methods: {
    api() {
      return new Api()
    },

    resetPricesData() {
      this.model.pricing = null
      this.dataLoaded = false
    },

    loadForm() {
      return this.api()
        .getCityTaxiPricingForm({
          company_id: this.$route.params.company_id,
        })
        .then((response) => {
          this.setFields(response.fields)

          this.setFieldGroup('standard', [
            'pricing.one_way',
            'pricing.return',
            'pricing.return_discount',
            'pricing.standing_fee',
            'pricing.minimum_price',
            'pricing.stopway_minimum',
          ])
          this.setFieldGroup('additional', [
            'pricing.meet_greet',
            'pricing.disabled_place',
            'pricing.baby_seat',
            'pricing.booster_seat',
            'pricing.ski_equipment_fee',
            'pricing.passenger_fee_min',
            'pricing.passenger_fee',
            'pricing.animal_fee',
            'pricing.waiting_time_per_minute',
          ])
          this.setFieldGroup('day_night', [
            'pricing.night_threshold.start',
            'pricing.night_threshold.end',
            'pricing.days_metric',
          ])

          this.updateCompanyFilters()

          this.formLoaded = true
        })
    },

    loadPrices() {
      if (!this.formLoaded) {
        return
      }

      this.resetPricesData()

      if (this.$allows('administer') && !this.model.company_id) {
        return
      }

      if (!this.model.vehicle_id || !this.model.division_id) {
        return
      }

      this.api()
        .getCityTaxiPricing({
          company_id: this.model.company_id,
          division_id: this.model.division_id,
          vehicle_id: this.model.vehicle_id,
        })
        .then(({ averages, pricing, currency_label }) => {
          this.model = { ...this.model, pricing }

          applyAverages(this.fields, averages, (value) =>
            this.$options.filters.currency(value, currency_label)
          )

          this.dataLoaded = true
        })
    },

    loadDivisions() {
      if (
        !this.formLoaded ||
        (this.$allows('administer') && this.model.company_id === null)
      ) {
        return
      }

      this.fields.byAttribute.division_id.options = []

      new Api()
        .getDivisions({
          context: 'pricingOptions',
          where: {
            company_id: this.model.company_id,
            division_id: this.currentDivision.id,
            ride_type: 'transfer',
          },
        })
        .then(({ data }) => {
          this.fields.byAttribute.division_id.options = data
        })
    },

    loadVehicles() {
      if (!this.formLoaded || !this.model.division_id) {
        return
      }

      this.fields.byAttribute.vehicle_id.options = []
      new Api()
        .getVehicles({
          context: 'pricingOptions',
          where: {
            company_id: this.model.company_id,
            division_id: this.model.division_id,
            ride_type: 'transfer',
          },
        })
        .then(({ data }) => {
          this.fields.byAttribute.vehicle_id.options = data
        })
    },

    save() {
      this.errors.clear()

      const promise = this.api()
        .updateCityTaxiPricing(this.model)
        .then((result) => {
          this.$toast.success(result.message)
          return result
        })
        .catch((response) => {
          this.onSaveError(response)
          return Promise.reject(response)
        })

      return this.$uiBlocker(promise, this.$el)
    },

    updateCompanyFilters() {
      if (this.fields.byAttribute['company_id']) {
        this.fields.byAttribute['company_id'].source.params = {
          ...this.fields.byAttribute['company_id'].source.params,
          filter: {
            division_id: this.currentDivision.id,
            has_division_pricing: this.has_pricing,
          },
        }
      }
    },


    onDivisionChange() {
      this.model.vehicle_id = null
    },

    onCompanyChange() {
      this.model.division_id = null
      this.model.vehicle_id = null
    },
  },
}
</script>
